import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, Handshake, Loader2, Pencil, Plus, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { hasPermission } from "../lib/permissions";
import { HISTORY_LABELS, PARTNER_STATE_LABELS, PARTNER_TYPES, canManagePartners, emptyPartnerForm, marketingError, newKey, partnerPayload, partnerProblem, partnerTypeLabel } from "../lib/marketing";
import { formatDateTime } from "../lib/tickets";
import { Field, OwnerSelect, StateBadge } from "../components/marketing/MarketingBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../components/ui/sheet";

const STATE_TABS = [["active", "Actifs"], ["ended", "Terminés"], ["all", "Tous"]];
const NO_AGREEMENT = "__none__";

// The agreement is a document of the register the person can already read; the list is the register's own (its visibility rules apply).
function AgreementSelect({ value, onChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/records/documents", { params: { limit: 50 } }).then(({ data }) => { if (alive) setItems(data.items || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const known = items.some((d) => d.id === value);
  return (
    <Select value={value || NO_AGREEMENT} onValueChange={(v) => onChange(v === NO_AGREEMENT ? "" : v)}>
      <SelectTrigger className="h-10" aria-label="Convention" data-testid="partner-agreement"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_AGREEMENT}>Aucune convention liée</SelectItem>
        {value && !known && <SelectItem value={value}>Document actuel</SelectItem>}
        {items.map((d) => <SelectItem key={d.id} value={d.id}>{d.number} — {d.title}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function PartnerDialog({ partner, canLinkRecord, onClose, onDone }) {
  const editing = !!partner;
  const [form, setForm] = useState(() => ({ ...emptyPartnerForm(partner), record_id: partner?.record_id || "" }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key, setKey] = useState(newKey);
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setKey(newKey()); };
  const problem = partnerProblem(form);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { ...partnerPayload(form), ...(canLinkRecord ? { record_id: form.record_id || null } : {}) };
      const { data } = editing
        ? await api.patch(`/marketing/partners/${partner.id}`, { ...body, expected_version: partner.version })
        : await api.post("/marketing/partners", body, { headers: { "Idempotency-Key": key } });
      toast.success(editing ? "Partenaire modifié" : "Partenaire ajouté");
      onDone(data);
    } catch (e) {
      const existing = e?.response?.data?.detail?.existing;
      setError(existing ? `Un partenaire porte déjà ce nom : ${existing.number} — ${existing.name}.` : marketingError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  const text = (id, label, rest = {}) => (
    <div className="space-y-1.5"><Label htmlFor={`par-${id}`}>{label}</Label>
      <Input id={`par-${id}`} value={form[id]} onChange={(e) => set({ [id]: e.target.value })} className="h-10" data-testid={`partner-${id}`} {...rest} /></div>
  );
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="partner-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier le partenaire" : "Nouveau partenaire"}</DialogTitle>
          <DialogDescription>{editing ? partner.number : "Un organisme avec lequel Jamora travaille. Saisissez un contact professionnel, jamais un numéro privé."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {text("name", "Nom de l'organisme", { maxLength: 160 })}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={form.type || undefined} onValueChange={(type) => set({ type })}>
                <SelectTrigger className="h-10" aria-label="Type" data-testid="partner-type"><SelectValue placeholder="Choisir un type" /></SelectTrigger>
                <SelectContent>{PARTNER_TYPES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Responsable</Label><OwnerSelect value={form.owner_id} onChange={(owner_id) => set({ owner_id })} testid="partner-owner" /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">{text("contact_name", "Personne de contact", { maxLength: 160 })}{text("contact_function", "Fonction", { maxLength: 160 })}</div>
          <div className="grid gap-3 sm:grid-cols-2">{text("contact_email", "E-mail professionnel", { maxLength: 160, type: "email" })}{text("contact_phone", "Téléphone professionnel", { maxLength: 40 })}</div>
          {canLinkRecord && <div className="space-y-1.5"><Label>Convention</Label><AgreementSelect value={form.record_id} onChange={(record_id) => set({ record_id })} /></div>}
          <div className="space-y-1.5"><Label htmlFor="par-notes">Notes</Label>
            <Textarea id="par-notes" rows={2} maxLength={1000} value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="partner-notes" /></div>
          {error && <p className="text-sm text-red-600" role="alert" data-testid="partner-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !!problem} title={problem || undefined} data-testid="partner-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{editing ? "Enregistrer" : "Ajouter"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StateDialog({ partner, onClose, onDone }) {
  const ending = partner.state === "active";
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/marketing/partners/${partner.id}/${ending ? "end" : "resume"}`, { expected_version: partner.version, reason: reason.trim() || null });
      toast.success(ending ? "Partenariat terminé" : "Partenariat repris");
      onDone(data);
    } catch (e) {
      setError(marketingError(e, "Impossible de continuer. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="partner-state-dialog">
        <DialogHeader>
          <DialogTitle>{ending ? `Terminer le partenariat avec ${partner.name} ?` : `Reprendre le partenariat avec ${partner.name} ?`}</DialogTitle>
          <DialogDescription>{ending ? "Le partenaire n'est plus proposé pour de nouvelles origines. Son historique est conservé : un partenaire n'est jamais supprimé." : "Le partenaire redevient proposé."}</DialogDescription>
        </DialogHeader>
        {ending && (
          <div className="space-y-1.5"><Label htmlFor="par-reason">Motif</Label>
            <Textarea id="par-reason" rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="partner-reason" /></div>
        )}
        {error && <p className="text-sm text-red-600" role="alert" data-testid="partner-state-error">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant={ending ? "destructive" : "default"} onClick={submit} disabled={busy || (ending && reason.trim().length < 3)} data-testid="partner-state-submit">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{ending ? "Terminer" : "Reprendre"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartnerSheet({ id, canManage, canLinkRecord, canOpenRecord, onClose, onChanged }) {
  const [partner, setPartner] = useState(null);
  const [phase, setPhase] = useState("loading");
  const { user } = useAuth();
  const [dialog, setDialog] = useState(null);
  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const { data } = await api.get(`/marketing/partners/${id}`);
      setPartner(data);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, [id]);
  useEffect(() => { if (id) load(); }, [id, load]);
  const changed = () => { setDialog(null); load(); onChanged(); };
  return (
    <Sheet open={!!id} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="partner-sheet">
        <SheetHeader>
          <SheetTitle className="pr-6 text-left">{partner?.name || "Partenaire"}</SheetTitle>
          <SheetDescription className="text-left">{partner ? `${partner.number} · ${partnerTypeLabel(partner.type)}` : ""}</SheetDescription>
        </SheetHeader>
        {phase === "loading" && <div className="space-y-2 py-4"><Skeleton className="h-6 w-full" /><Skeleton className="h-24 w-full" /></div>}
        {phase === "error" && <p className="py-4 text-sm text-slate-600" data-testid="partner-sheet-error">Impossible de charger ce partenaire.<Button variant="link" onClick={load}>Réessayer</Button></p>}
        {phase === "ready" && partner && (
          <div className="space-y-5 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <StateBadge label={PARTNER_STATE_LABELS[partner.state]} tone={partner.state === "active" ? "emerald" : "slate"} testid="partner-state" />
              {canManage && <Button variant="outline" size="sm" onClick={() => setDialog("edit")} data-testid="partner-edit"><Pencil size={14} className="mr-1.5" />Modifier</Button>}
              {canManage && <Button variant="outline" size="sm" onClick={() => setDialog("state")} data-testid="partner-toggle">{partner.state === "active" ? "Terminer" : "Reprendre"}</Button>}
              {canManage && hasPermission(user, "marketing:delete") && <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDialog("delete")} data-testid="partner-delete">Supprimer…</Button>}
            </div>
            <dl className="divide-y divide-slate-100">
              <Field label="Responsable">{partner.owner.name || "—"}</Field>
              {partner.contact_name && <Field label="Contact">{partner.contact_name}{partner.contact_function ? ` · ${partner.contact_function}` : ""}</Field>}
              {partner.contact_email && <Field label="E-mail"><a className="text-jamora hover:underline" href={`mailto:${partner.contact_email}`}>{partner.contact_email}</a></Field>}
              {partner.contact_phone && <Field label="Téléphone">{partner.contact_phone}</Field>}
              {partner.record_id && canOpenRecord && <Field label="Convention"><Link className="text-jamora hover:underline" to={`/documents/${partner.record_id}`} data-testid="partner-agreement-link">Ouvrir le document</Link></Field>}
              {partner.notes && <Field label="Notes"><span className="whitespace-pre-wrap">{partner.notes}</span></Field>}
            </dl>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Historique</h3>
              <ol className="space-y-2" data-testid="partner-history">
                {partner.history.map((h) => (
                  <li key={h.id} className="border-l-2 border-slate-200 pl-3">
                    <div className="text-sm text-slate-900">{HISTORY_LABELS[h.kind] || h.kind}</div>
                    <div className="text-xs text-slate-500">{formatDateTime(h.at)}{h.actor_name ? ` · ${h.actor_name}` : ""}</div>
                    {h.reason && <div className="text-xs text-slate-600">Motif : {h.reason}</div>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
        <DeleteWithReasonDialog
          target={dialog === "delete" ? partner : null} testId="delete-partner" onClose={() => setDialog(null)}
          title={`Supprimer le partenaire ${partner?.number} ?`}
          description="Il va dans la corbeille et se restaure pendant 30 jours. Un partenaire auquel des pharmacies sont rattachées se termine à la place."
          onConfirm={async (reason) => {
            try {
              await api.delete(`/marketing/partners/${partner.id}`, { data: { reason } });
              toast.success("Mis à la corbeille");
              onClose();
              onChanged();
            } catch (e) {
              toast.error(marketingError(e, "Impossible de supprimer cet élément."));
              setDialog(null);
            }
          }}
        />
        {dialog === "edit" && partner && <PartnerDialog partner={partner} canLinkRecord={canLinkRecord} onClose={() => setDialog(null)} onDone={changed} />}
        {dialog === "state" && partner && <StateDialog partner={partner} onClose={() => setDialog(null)} onDone={changed} />}
      </SheetContent>
    </Sheet>
  );
}

export default function MarketingPartners() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const state = STATE_TABS.some(([id]) => id === params.get("state")) ? params.get("state") : "active";
  const search = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const seq = useRef(0);
  const canManage = canManagePartners(user);
  const canOpenRecord = !!user?.features?.records && hasPermission(user, "records:view");

  const update = (changes) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k); }
    return next;
  }, { replace: true });
  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== search) update({ q: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => ({ limit: 200, state, ...(search ? { q: search } : {}) }), [state, search]);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { data: body } = await api.get("/marketing/partners", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(marketingError(e, "Impossible de charger les partenaires."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const filtered = !!search;
  const reset = () => { setSearchInput(""); setParams({}, { replace: true }); };

  return (
    <div className="space-y-4" data-testid="marketing-partners-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Partenaires</h1>
          <p className="text-sm text-slate-500">Les organismes qui font connaître Jamora aux pharmacies : contact professionnel, responsable et convention.</p>
        </div>
        {canManage && <Button onClick={() => setCreating(true)} data-testid="partner-new"><Plus size={16} className="mr-1.5" />Nouveau partenaire</Button>}
      </div>

      <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Partenaires">
        {STATE_TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={state === id} onClick={() => update({ state: id === "active" ? "" : id })} data-testid={`partners-tab-${id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${state === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{label}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Nom du partenaire…" className="pl-9 h-9" aria-label="Rechercher un partenaire" data-testid="partners-search" />
        </div>
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="partners-empty">
          <Handshake className="mx-auto text-slate-300" size={26} />
          {filtered ? <><h3 className="font-medium text-slate-900">Aucun partenaire ne correspond</h3><Button variant="outline" size="sm" onClick={reset}>Effacer la recherche</Button></> : (
            <><h3 className="font-medium text-slate-900">{state === "ended" ? "Aucun partenariat terminé" : "Aucun partenaire pour le moment"}</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">{canManage ? "Ajoutez les organismes qui vous recommandent : vous saurez combien de pharmacies chacun a amenées." : "Les partenaires apparaîtront ici."}</p>
              {canManage && state !== "ended" && <Button size="sm" onClick={() => setCreating(true)}><Plus size={14} className="mr-1.5" />Nouveau partenaire</Button>}</>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="partners-table">
            <Table>
              <TableHeader><TableRow><TableHead>Partenaire</TableHead><TableHead>Type</TableHead><TableHead>Responsable</TableHead><TableHead className="hidden lg:table-cell">Contact</TableHead><TableHead>État</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => setOpen(p.id)} data-testid={`partner-row-${p.number}`}>
                    <TableCell><div className="font-medium text-slate-900">{p.name}</div><div className="text-xs text-slate-500 tabular-nums">{p.number}</div></TableCell>
                    <TableCell className="text-sm">{partnerTypeLabel(p.type)}</TableCell>
                    <TableCell className="text-sm">{p.owner.name || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{p.contact_name || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell><StateBadge label={PARTNER_STATE_LABELS[p.state]} tone={p.state === "active" ? "emerald" : "slate"} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="partners-cards">
            {data.items.map((p) => (
              <button key={p.id} type="button" onClick={() => setOpen(p.id)} className="flex w-full items-start gap-3 px-4 py-3 text-left" data-testid={`partner-card-${p.number}`}>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="font-medium text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.number} · {partnerTypeLabel(p.type)}</div>
                  <div className="text-sm text-slate-700">{p.owner.name || "—"}</div>
                  <StateBadge label={PARTNER_STATE_LABELS[p.state]} tone={p.state === "active" ? "emerald" : "slate"} />
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </button>
            ))}
          </Card>
          <p className="text-xs text-slate-500" data-testid="partners-count">{data.total} partenaire{data.total > 1 ? "s" : ""}</p>
        </>
      )}

      {creating && <PartnerDialog canLinkRecord={canOpenRecord} onClose={() => setCreating(false)} onDone={(p) => { setCreating(false); load(); setOpen(p.id); }} />}
      <PartnerSheet id={open} canManage={canManage} canLinkRecord={canOpenRecord} canOpenRecord={canOpenRecord} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
